/**
 * AST Node Type Constants
 *
 * Single source of truth for all AST node type strings.
 * Use these constants instead of string literals to ensure type safety
 * and make refactoring easier.
 *
 * @example
 * ```typescript
 * import { AST_TYPES } from './constants/ast-types';
 *
 * const node = {
 *   type: AST_TYPES.HIGHLIGHTED_TEXT,
 *   value: "123"
 * };
 * ```
 */

/**
 * AST Node Types
 *
 * These are the internal AST representation types used during parsing.
 */
export const AST_TYPES = {
  // ═══════════════════════════════════════════════════════════════════════════
  // Inline Elements
  // ═══════════════════════════════════════════════════════════════════════════

  /** Regular text without special formatting */
  FREE_TEXT: 'FreeText',

  /** Explicit spacing element */
  SPACE: 'Space',

  /** Text with optional character-range highlighting */
  HIGHLIGHTED_TEXT: 'HighlightedText',

  /** Text with color and optional character-range coloring */
  COLOR_TEXT: 'ColorText',

  /** Bold text with optional character-range bolding */
  BOLD_TEXT: 'BoldText',

  /** Inline input widget (string or numeric) */
  INLINE_INPUT: 'InlineInput',

  /** Single inline draggable option (appears in-flow with text) */
  INLINE_DRAG_OPTION: 'InlineDragOption',

  /** Inline fraction widget (numerator/denominator with optional inputs) */
  FRACTION: 'Fraction',

  /** Inline image element */
  IMAGE: 'Image',

  /** Clock widget — analog/digital, display or interactive */
  CLOCK: 'Clock',

  // ═══════════════════════════════════════════════════════════════════════════
  // Blocks
  // ═══════════════════════════════════════════════════════════════════════════

  /** Question/instruction text block */
  TEXT_BLOCK: 'TextBlock',

  /** Line containing inline elements */
  LINE_BLOCK: 'LineBlock',

  /** Table layout block */
  TABLE_BLOCK: 'TableBlock',

  /** Row within a table */
  TABLE_ROW: 'TableRow',

  /** Cell within a table row */
  TABLE_CELL: 'TableCell',

  /** Vertical operation (column arithmetic) block */
  VERTICAL_OPERATION_BLOCK: 'VerticalOperationBlock',

  /** Row within a vertical operation */
  VOP_ROW: 'VopRow',

  /** Cell within a vertical-operation row */
  VOP_CELL: 'VopCell',

  /** Separator line within a vertical operation */
  VOP_SEPARATOR: 'VopSeparator',

  /** Carry row within a vertical operation */
  VOP_CARRY_ROW: 'VopCarryRow',

  /** Number line layout block */
  NUMBER_LINE_BLOCK: 'NumberLineBlock',

  /** A position marker on the number line */
  NUMBER_LINE_POSITION: 'NumberLinePosition',

  /** A draggable cursor on the number line */
  NUMBER_LINE_CURSOR: 'NumberLineCursor',

  /** A dynamic subdivision counter embedded in a number line */
  NUMBER_LINE_COUNTER: 'NumberLineCounter',

  /** A highlighted range on the number line axis */
  NUMBER_LINE_HIGHLIGHT: 'NumberLineHighlight',

  /** An arrow annotation spanning a number line interval */
  NUMBER_LINE_ARROW: 'NumberLineArrow',

  /** Matcher layout block */
  MATCHER_BLOCK: 'MatcherBlock',

  /** Row within a matcher */
  MATCHER_ROW: 'MatcherRow',

  /** Item within a matcher row */
  MATCHER_ITEM: 'MatcherItem',

  /** Pair-matcher layout block (top-row ↔ bottom-row pairing) */
  PAIR_MATCHER_BLOCK: 'PairMatcherBlock',

  /** Visual fraction inline widget */
  VISUAL_FRACTION: 'VisualFraction',

  // ═══════════════════════════════════════════════════════════════════════════
  // Tools
  // ═══════════════════════════════════════════════════════════════════════════

  /** Drag-and-drop options tool */
  DRAG_OPTIONS: 'DragOptions',

  /** Radio button options tool */
  RADIO_OPTIONS: 'RadioOptions',

  /** Numeric keypad input tool */
  NUMERIC_KEYPAD: 'NumericKeypad',

  /** Geometry ruler tool */
  GEOMETRY_RULER: 'GeometryRuler',

  /** Geometry protractor tool */
  GEOMETRY_PROTRACTOR: 'GeometryProtractor',

  /** Geometry set square tool */
  GEOMETRY_SET_SQUARE: 'GeometrySetSquare',

  // ═══════════════════════════════════════════════════════════════════════════
  // Validation
  // ═══════════════════════════════════════════════════════════════════════════

  /** Simple answer matching validation */
  SIMPLE_VALIDATION: 'SimpleValidation',

  /** Expression-based validation */
  EXPRESSION_VALIDATION: 'ExpressionValidation',

  /** Mixed validation: per-widget simple answers + expression over free variables */
  MIXED_VALIDATION: 'MixedValidation',

  // ═══════════════════════════════════════════════════════════════════════════
  // Document
  // ═══════════════════════════════════════════════════════════════════════════

  /** Root exercise document */
  EXERCISE_DOCUMENT: 'ExerciseDocument',
} as const;

/**
 * Type helper to get all AST type values as a union type
 */
export type ASTType = typeof AST_TYPES[keyof typeof AST_TYPES];
